import React from 'react';
import { Card, Select, Input, Slider, InputNumber, Switch, Typography, Space, Row, Col, Button } from 'antd';
import { SettingOutlined, ThunderboltOutlined, FileTextOutlined, GlobalOutlined, EyeOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const SIGN_CATEGORIES = {
  'Advance Direction Signs (16.01-16.03)': [
    { name: 'Stack Type Advance Direction', section: 'Fig.16.01', desc: 'Shoulder-mounted stack with arrows & destinations', type: 'shoulder' },
    { name: 'Cautionary + Regulatory Advance', section: 'Fig.16.01a', desc: 'Advance direction with cautionary/regulatory elements', type: 'regulatory_advance' },
    { name: 'Map Type Direction', section: 'Fig.16.02', desc: 'Junction/map configuration for complex roads', type: 'map_type' },
    { name: 'Map Type Advance', section: 'Fig.16.02', desc: 'Map type advance direction sign', type: 'map_type_advance' },
    { name: 'Map Type on Roundabout', section: 'Fig.16.03', desc: 'Map type with roundabout configuration', type: 'roundabout' },
  ],
  'Flag / Reassurance / Place (16.04-16.06)': [
    { name: 'Flag Type Direction', section: 'Fig.16.04', desc: 'Pointed flag-shape directional sign', type: 'flag_type' },
    { name: 'Reassurance Sign', section: 'Fig.16.05', desc: 'Destinations with confirming distances (km)', type: 'reassurance' },
    { name: 'Place Identification', section: 'Fig.16.06', desc: 'Single place name boundary/identification', type: 'place_identification' },
  ],
  'Facility Signs (16.07-16.09)': [
    { name: 'Truck Lay-by', section: 'Fig.16.07', desc: 'Posted at 250m and 500m ahead', type: 'truck_layby' },
    { name: 'Toll Booth Ahead', section: 'Fig.16.08', desc: 'Toll booth ahead facility sign', type: 'toll_booth' },
    { name: 'Weigh Bridge Ahead', section: 'Fig.16.09', desc: 'Weigh bridge ahead with direction arrow', type: 'weigh_bridge' },
  ],
  'Gantry Mounted Signs (16.10-16.11, 16.16)': [
    { name: 'Gantry (Grade-Separated)', section: 'Fig.16.10', desc: 'Gantry ahead of a grade-separated junction', type: 'gantry' },
    { name: 'Gantry (At-Grade Junction)', section: 'Fig.16.11', desc: 'Gantry ahead of an at-grade junction', type: 'gantry_at_grade' },
    { name: 'Gantry (Flyover, Urban)', section: 'Fig.16.16', desc: 'Gantry ahead of a flyover on urban/city roads', type: 'gantry_flyover' },
  ],
  'Interchange Signs (16.12)': [
    { name: 'Advance Interchange', section: 'Fig.16.12', desc: 'Advance direction sign for an interchange', type: 'advance_interchange' },
    { name: 'Advance Interchange (Variant 2)', section: 'Fig.16.12', desc: 'Interchange sign variant 2', type: 'advance_interchange_2' },
    { name: 'Advance Interchange (Variant 3)', section: 'Fig.16.12', desc: 'Interchange sign variant 3', type: 'advance_interchange_3' },
    { name: 'Advance Interchange (Double)', section: 'Fig.16.12', desc: 'Interchange sign with double off-shoot', type: 'advance_interchange_double' },
  ],
  'Lane / Grade / Expressway (16.13-16.15)': [
    { name: 'Lane Dedicated Gantry', section: 'Fig.16.13', desc: 'Lane-dedicated gantry signs', type: 'lane_dedicated_gantry' },
    { name: 'Lane Dedicated Gantry (Single)', section: 'Fig.16.13', desc: 'Single-board lane-dedicated gantry', type: 'lane_dedicated_gantry_single' },
    { name: 'Shoulder Advance (Grade-Sep)', section: 'Fig.16.14', desc: 'Shoulder sign in advance of a grade-separated junction', type: 'grade_separated_advance' },
    { name: 'Expressway Ahead', section: 'Fig.16.15', desc: 'Expressway ahead sign', type: 'expressway_ahead' },
  ],
  'Supplementary (16.17)': [
    { name: 'Definition / Supplementary Plate', section: 'Fig.16.17', desc: 'NO PARKING / NO STOPPING supplementary plate', type: 'supplementary' },
  ],
  'Tourism Signs (16.18-16.20)': [
    { name: 'Tourist Destination Sign', section: 'Fig.16.18', desc: 'Tourist destination direction (no photo)', type: 'tourist' },
    { name: 'Tourist Photo Sign', section: 'Fig.16.19', desc: 'Tourist sign with photograph', type: 'tourist_photo' },
    { name: 'Finger Destination (Pedestrian)', section: 'Fig.16.20', desc: 'Finger destination direction sign', type: 'right_finger_destination' },
  ],
  'Project / Bilingual Signs': [
    { name: 'Overhead Sign', section: '16.3.9', desc: 'Generic gantry-mounted overhead sign', type: 'overhead' },
    { name: 'Bilingual Project Sign', section: '16.3.1', desc: 'Bilingual project information sign (900\u00d71200mm)', type: 'bilingual' },
    { name: 'Trilingual Project Sign', section: '16.3.1', desc: 'Trilingual project information sign (900\u00d71200mm)', type: 'trilingual' },
  ],
};

const MOUNTING_FORMS = [
  { name: 'Roadside', char: 'Side-mounted signs over the verge', usage: 'All types of roads with adequate roadside space' },
  { name: 'Cantilever', char: 'Overhead signs partially over carriageway', usage: 'More economical for smaller directional signs' },
  { name: 'Gantry', char: 'Overhead signs spanning over carriageway', usage: 'Larger signs, heavy traffic, ≥3 lanes' },
  { name: 'Double/Butterfly', char: 'Overhead on separator/divider', usage: 'Low approach speed or crash cushion assured' },
];

const ROUTE_TYPES = ['NH', 'SH', 'MDR', 'ODR', 'Expressway', 'City Road'];

const SPEED_RULES_STANDARD = [
  { min: 0, max: 30, lower: 70, upper: 100, desc: 'Urban/Low Speed' },
  { min: 31, max: 50, lower: 100, upper: 140, desc: 'Urban Roads' },
  { min: 51, max: 65, lower: 125, upper: 175, desc: 'State Highways' },
  { min: 66, max: 80, lower: 175, upper: 245, desc: 'National Highways' },
  { min: 81, max: 100, lower: 215, upper: 300, desc: 'Expressways' },
  { min: 101, max: 120, lower: 305, upper: 425, desc: 'High-Speed Corridors' },
  { min: 121, max: 150, lower: 340, upper: 475, desc: 'Access Controlled' },
];

const SPEED_RULES_OVERHEAD = [
  { min: 0, max: 80, lower: 210, upper: 290, desc: 'Standard Overhead' },
  { min: 81, max: 100, lower: 250, upper: 350, desc: 'Expressway Overhead' },
  { min: 101, max: 120, lower: 330, upper: 460, desc: 'High-Speed Overhead' },
  { min: 121, max: 150, lower: 380, upper: 530, desc: 'Access Controlled Overhead' },
];

const OVERHEAD_SIGN_TYPES = ['bilingual', 'trilingual'];

function getSpeedRules(signType) {
  return OVERHEAD_SIGN_TYPES.includes(signType) ? SPEED_RULES_OVERHEAD : SPEED_RULES_STANDARD;
}

export default function ConfigPanel({ config, onChange }) {
  const speedRules = getSpeedRules(config.sign_type);
  const speedRule = speedRules.find(r => config.design_speed >= r.min && config.design_speed <= r.max) || speedRules[0];

  const allSignTypes = Object.values(SIGN_CATEGORIES).flat();
  const selectedCategory = Object.entries(SIGN_CATEGORIES).find(([, types]) => types.some(t => t.type === config.sign_type))?.[0] || Object.keys(SIGN_CATEGORIES)[0];
  const filteredSignTypes = SIGN_CATEGORIES[selectedCategory] || [];
  const currentSign = allSignTypes.find(s => s.type === config.sign_type);
  const currentMount = MOUNTING_FORMS.find(m => m.name === config.mounting_form) || MOUNTING_FORMS[0];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      {/* Sign Type */}
      <Card 
        size="small"
        title={
          <Space>
            <SettingOutlined />
            <Text strong>Sign Type (Section 16)</Text>
          </Space>
        }
        className="section-card"
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Category</Text>
            <Select
              value={selectedCategory}
              onChange={(value) => {
                const first = SIGN_CATEGORIES[value]?.[0];
                if (first) onChange({ sign_type: first.type });
              }}
              style={{ width: '100%' }}
              size="large"
            >
              {Object.keys(SIGN_CATEGORIES).map(cat => (
                <Select.Option key={cat} value={cat}>{cat}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Sign Type</Text>
            <Select
              value={config.sign_type}
              onChange={(value) => onChange({ sign_type: value })}
              style={{ width: '100%' }}
              size="large"
            >
              {filteredSignTypes.map((s, index) => (
                <Select.Option key={`${s.type}-${s.section}-${index}`} value={s.type}>
                  {s.name} ({s.section})
                </Select.Option>
              ))}
            </Select>
            {currentSign && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                {currentSign.desc}
              </Text>
            )}
          </div>
        </Space>
      </Card>

      {/* Show Dimensions Toggle */}
      <Card 
        size="small"
        title={
          <Space>
            <EyeOutlined />
            <Text strong>Show Dimensions</Text>
          </Space>
        }
        className="section-card"
      >
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>Overlay dimension annotations on the generated SVG</Text>
          <Switch
            checked={config.show_dimensions}
            onChange={(checked) => onChange({ show_dimensions: checked })}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </Space>
      </Card>

      {/* Design Speed */}
      <Card 
        size="small"
        title={
          <Space>
            <ThunderboltOutlined />
            <Text strong>Design Speed (Table 8.3)</Text>
          </Space>
        }
        className="section-card"
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={12} align="middle">
            <Col flex="1">
              <Slider
                min={0}
                max={150}
                value={config.design_speed}
                onChange={(value) => onChange({ design_speed: value })}
                tooltip={{ formatter: (value) => `${value} km/h` }}
              />
            </Col>
            <Col>
              <Space.Compact>
                <InputNumber
                  min={0}
                  max={150}
                  value={config.design_speed}
                  onChange={(value) => onChange({ design_speed: value || 0 })}
                  style={{ width: 100 }}
                  size="small"
                />
                <Button size="small" style={{ pointerEvents: 'none' }}>km/h</Button>
              </Space.Compact>
            </Col>
          </Row>
          <Space>
            <Text type="secondary" style={{ fontSize: 11 }}>{speedRule.desc}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              x-height: {speedRule.lower}mm / {speedRule.upper}mm
            </Text>
          </Space>
        </Space>
      </Card>

      {/* Mounting Form */}
      <Card 
        size="small"
        title={
          <Space>
            <FileTextOutlined />
            <Text strong>Mounting Form (Table 16.1)</Text>
          </Space>
        }
        className="section-card"
      >
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <Select
            value={config.mounting_form}
            onChange={(value) => onChange({ mounting_form: value })}
            style={{ width: '100%' }}
            size="large"
          >
            {MOUNTING_FORMS.map(m => (
              <Select.Option key={m.name} value={m.name}>{m.name}</Select.Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
            {currentMount.char}
          </Text>
          <Text style={{ fontSize: 11, color: 'var(--teal)', display: 'block' }}>
            {currentMount.usage}
          </Text>
        </Space>
      </Card>

      {/* Route Info */}
      <Card 
        size="small"
        title={
          <Space>
            <GlobalOutlined />
            <Text strong>Route Information</Text>
          </Space>
        }
        className="section-card"
      >
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Route Type</Text>
            <Select
              value={config.route_type}
              onChange={(value) => onChange({ route_type: value })}
              style={{ width: '100%' }}
              size="large"
            >
              {ROUTE_TYPES.map(r => (
                <Select.Option key={r} value={r}>{r}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Route Number</Text>
            <Input
              placeholder="e.g. 48, 8A"
              value={config.route_number}
              onChange={(e) => onChange({ route_number: e.target.value })}
              size="large"
            />
          </Col>
        </Row>
      </Card>
    </Space>
  );
}
